import React, { useState, useEffect } from 'react';
import { onboardingAPI } from '../../api';

function AgreementReview({ token, formData, onNext, onBack, setVersionHash, packageType }) {
  const isStarter = packageType === 'Starter';
  const isBASOnly = packageType === 'BAS Only';
  const isOneOff = packageType === 'Start-up Pack' || packageType === 'Clean Slate';
  const oneOffItems = formData.pricing?.one_off_items || [];
  const oneOffTotal = oneOffItems.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfReady, setPdfReady] = useState(false);

  useEffect(() => {
    loadAgreementPreview();
  }, []);

  const loadAgreementPreview = async () => {
    try {
      const response = await onboardingAPI.previewAgreement(token);
      setVersionHash(response.data.version_hash);
      setPdfReady(response.data.preview_available);
      setError('');
    } catch (err) {
      setError('Failed to generate agreement preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card fade-in" style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
        <p>Generating your personalized agreement...</p>
      </div>
    );
  }

  return (
    <div className="card fade-in">
      <h2>Review Your Agreement</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
        Please review the Client Agreement{(isStarter || isBASOnly || isOneOff) ? '' : ' and BAS Agent Authority'} below. This document has been personalized with your information.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Agreement Summary */}
      <div style={{ 
        backgroundColor: 'var(--bg-gray-50)', 
        border: '1px solid var(--bg-gray-200)',
        borderRadius: '0.375rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ marginBottom: '1rem' }}>Agreement Summary</h3>
        
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <strong style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Business Name:</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>{formData.business_name}</p>
          </div>
          
          <div>
            <strong style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>ABN:</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>{formData.abn}</p>
          </div>
          
          <div>
            <strong style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Main Contact:</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>{formData.contacts?.name} ({formData.contacts?.email})</p>
          </div>
          
          <div>
            <strong style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              {isOneOff ? 'One-Off Service Fee:' : 'Monthly Service Fee:'}
            </strong>
            {isOneOff ? (
              <div style={{ margin: '0.25rem 0 0 0' }}>
                {oneOffItems.map((item, idx) => (
                  <div key={idx} style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    • {item.label}: ${parseFloat(item.amount || 0).toFixed(2)}
                  </div>
                ))}
                <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>
                  Total: ${oneOffTotal.toFixed(2)} AUD (excl. GST)
                </p>
              </div>
            ) : (
              <p style={{ margin: '0.25rem 0 0 0' }}>${formData.pricing?.monthly_total} AUD (excl. GST)</p>
            )}
          </div>
          
          <div>
            <strong style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Software:</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>{formData.software?.subscription}</p>
          </div>
          
          {!isStarter && !isOneOff && (
          <div>
            <strong style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>BAS Lodgement:</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>{formData.compliance?.bas_frequency}</p>
          </div>
          )}
        </div>
      </div>

      {/* Document Description */}
      <div className="alert alert-info" style={{ marginBottom: '2rem' }}>
        <strong>📝 This Agreement Includes:</strong>
        <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: '0' }}>
          <li><strong>Client Agreement:</strong> Terms of service, responsibilities, and fees</li>
          {!isStarter && !isBASOnly && !isOneOff && (
            <li><strong>BAS Agent Authority:</strong> Authorization for ST Books to act as your BAS agent with the ATO</li>
          )}
        </ul>
      </div>

      {/* Key Terms Highlights */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>Key Terms</h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-gray-50)', borderRadius: '0.375rem' }}>
            <strong>Services Provided:</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              {isOneOff
                ? `One-off engagement covering: ${oneOffItems.map(i => i.label).join(', ') || 'agreed services'}`
                : isStarter 
                  ? 'Bookkeeping, end of financial year reporting'
                  : isBASOnly
                    ? 'BAS preparation and lodgement, GST reconciliation'
                    : `Bookkeeping, BAS preparation and lodgement${formData.services?.payroll ? ', Payroll services' : ''}, GST reconciliation, end of financial year reporting`
              }
            </p>
          </div>
          
          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-gray-50)', borderRadius: '0.375rem' }}>
            <strong>Your Responsibilities:</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              {isStarter
                ? 'Provide timely and accurate source documents, maintain secure backups of business records'
                : isBASOnly
                  ? 'Provide timely and accurate source documents, review and approve BAS lodgements'
                  : 'Provide timely and accurate source documents, review and approve BAS lodgements, maintain secure backups of business records'
              }
            </p>
          </div>
          
          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-gray-50)', borderRadius: '0.375rem' }}>
            <strong>Payment Terms:</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              Invoices sent monthly, due within 7 days. Late payments incur $25 (excl. GST) fee.
            </p>
          </div>
          
          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-gray-50)', borderRadius: '0.375rem' }}>
            <strong>Termination:</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              Either party may terminate with 30 days written notice
            </p>
          </div>
        </div>
      </div>

      {/* Compliance Notice */}
      <div className="alert alert-success">
        <strong>✓ Compliance & Standards</strong>
        <p style={{ margin: '0.5rem 0 0 0' }}>
          ST Books Pty Ltd is registered with the Tax Practitioners Board (BAS Agent #26378315) and adheres to 
          the TASA2009 Code of Conduct. We are members of the Institute of Certified Bookkeepers (ICB).
        </p>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button 
          type="button" 
          className="btn btn-outline"
          data-testid="back-button"
          onClick={onBack}
        >
          ← Back
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          data-testid="proceed-to-sign-button"
          onClick={() => onNext({})}
          style={{ flex: 1 }}
          disabled={!pdfReady}
        >
          Proceed to Sign →
        </button>
      </div>

      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '1rem', textAlign: 'center' }}>
        The full agreement will be available for download after signing
      </p>
    </div>
  );
}

export default AgreementReview;